import React, { useState, useEffect } from "react";
import Sidebar from "../Sidebar/Sidebar";
import Navbar from "./Navbar";
import Header from "./header";
import Dialog from "@mui/material/Dialog";
import "./Member.css";
import Switch from "@mui/material/Switch";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import AddIcon from "@mui/icons-material/Add";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CategoryIcon from "@mui/icons-material/Category";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ApiConfig from "../../common/config/ApiConfig";
import { useLanguage } from "../../common/i18n/LanguageContext";

// LottoType.startTime/endTime were removed server-side — rounds/round_date
// (a per-round, per-weekday/monthday sale schedule) fully replaces them.
// days_of_week follows JS Date.getDay(): 0=Sun..6=Sat.
const WEEKDAY_LABEL_KEYS = [
  "weekday_sun",
  "weekday_mon",
  "weekday_tue",
  "weekday_wed",
  "weekday_thu",
  "weekday_fri",
  "weekday_sat",
];
const DAYS_OF_MONTH = Array.from({ length: 31 }, (_, i) => i + 1);

const emptyRule = () => ({
  mode: "days_of_month",
  daysOfMonth: [],
  daysOfWeek: [],
  start_time: "00:00",
  end_time: "23:00",
});

const emptyEditForm = { id: null, name: "", rounds: [] };

const formatRule = (rule, t) => {
  const dayLabel =
    rule.days_of_month && rule.days_of_month.length > 0
      ? `${t("lotto_service_day_of_month_prefix")} ${rule.days_of_month.join(",")}`
      : rule.days_of_week && rule.days_of_week.length > 0
        ? rule.days_of_week.map((d) => t(WEEKDAY_LABEL_KEYS[d])).join(",")
        : "-";
  return `${dayLabel} (${rule.start_time}-${rule.end_time})`;
};

function LottoService() {
  const { t } = useLanguage();
  const [lottoTypes, setLottoTypes] = useState([]);
  const [reload, setReload] = useState(0);
  // Which lotto type's reward-type groups are currently expanded inline
  // (one at a time), plus a per-lotto-type cache of fetched groups so
  // re-expanding doesn't always re-fetch.
  const [expandedId, setExpandedId] = useState(null);
  const [groupsByLottoTypeId, setGroupsByLottoTypeId] = useState({});
  const [groupsLoading, setGroupsLoading] = useState(false);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState(emptyEditForm);

  const fetchLottoTypes = () => {
    ApiConfig.get("/getLottoTypeList").then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      setLottoTypes(Array.isArray(result.data) ? result.data : []);
    });
  };

  useEffect(() => {
    fetchLottoTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  const handleToggleStatus = (lottoType, checked) => {
    Swal.fire({
      title: t("common_are_you_sure"),
      text: `${t("lotto_service_toggle_text_prefix")} ${
        checked
          ? t("lotto_service_action_open")
          : t("lotto_service_action_close")
      } ${t("lotto_service_toggle_text_suffix")} "${lottoType.name}"`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: t("lotto_service_confirm_update"),
      cancelButtonText: t("common_cancel"),
    }).then((result) => {
      if (!result.isConfirmed) return;
      ApiConfig.post("/updateLottoType", {
        id: lottoType.id,
        status: checked,
      }).then((res) => {
        if (res.resCode !== 200) {
          toast.error(res.message || t("lotto_service_update_status_error"), {
            position: "top-right",
            autoClose: 3000,
          });
          return;
        }
        toast.success(t("lotto_service_update_status_success"), {
          autoClose: 3000,
        });
        setReload((r) => r + 1);
      });
    });
  };

  const handleCreate = () => {
    Swal.fire({
      title: t("lotto_service_add_title"),
      html: `
        <input id="swal-name" class="swal2-input" placeholder="${t("lotto_service_name_label")}" />
        <input id="swal-type" class="swal2-input" placeholder="${t("lotto_service_type_code_placeholder")}" />
      `,
      showCancelButton: true,
      confirmButtonText: t("common_create"),
      cancelButtonText: t("common_cancel"),
      preConfirm: () => {
        const name = document.getElementById("swal-name").value.trim();
        const type = document.getElementById("swal-type").value.trim();
        if (!name) {
          Swal.showValidationMessage(t("lotto_service_name_required"));
          return false;
        }
        if (!type) {
          Swal.showValidationMessage(t("lotto_service_type_code_required"));
          return false;
        }
        return { name, type };
      },
    }).then((result) => {
      if (!result.isConfirmed) return;
      ApiConfig.post("/createLottoType", result.value).then((res) => {
        if (res.resCode !== 200) {
          toast.error(res.message || t("lotto_service_add_error"), {
            position: "top-right",
            autoClose: 3000,
          });
          return;
        }
        toast.success(t("lotto_service_add_success"), { autoClose: 2000 });
        setReload((r) => r + 1);
      });
    });
  };

  const openEdit = (lottoType) => {
    setEditForm({
      id: lottoType.id,
      name: lottoType.name,
      rounds: (lottoType.rounds || []).map((r) => ({
        round: r.round,
        rules: (r.round_date || []).map((rd) => ({
          mode:
            rd.days_of_month && rd.days_of_month.length > 0
              ? "days_of_month"
              : "days_of_week",
          daysOfMonth: rd.days_of_month || [],
          daysOfWeek: rd.days_of_week || [],
          start_time: rd.start_time || "00:00",
          end_time: rd.end_time || "23:00",
        })),
      })),
    });
    setEditDialogOpen(true);
  };

  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setEditForm(emptyEditForm);
  };

  const addRound = () => {
    setEditForm((f) => ({
      ...f,
      rounds: [
        ...f.rounds,
        { round: f.rounds.length + 1, rules: [emptyRule()] },
      ],
    }));
  };

  const removeRound = (roundIdx) => {
    setEditForm((f) => ({
      ...f,
      rounds: f.rounds.filter((_, i) => i !== roundIdx),
    }));
  };

  const updateRoundNumber = (roundIdx, value) => {
    setEditForm((f) => ({
      ...f,
      rounds: f.rounds.map((r, i) =>
        i === roundIdx ? { ...r, round: value } : r,
      ),
    }));
  };

  const addRule = (roundIdx) => {
    setEditForm((f) => ({
      ...f,
      rounds: f.rounds.map((r, i) =>
        i === roundIdx ? { ...r, rules: [...r.rules, emptyRule()] } : r,
      ),
    }));
  };

  const removeRule = (roundIdx, ruleIdx) => {
    setEditForm((f) => ({
      ...f,
      rounds: f.rounds.map((r, i) =>
        i === roundIdx
          ? { ...r, rules: r.rules.filter((_, j) => j !== ruleIdx) }
          : r,
      ),
    }));
  };

  const updateRule = (roundIdx, ruleIdx, patch) => {
    setEditForm((f) => ({
      ...f,
      rounds: f.rounds.map((r, i) =>
        i === roundIdx
          ? {
              ...r,
              rules: r.rules.map((rule, j) =>
                j === ruleIdx ? { ...rule, ...patch } : rule,
              ),
            }
          : r,
      ),
    }));
  };

  const toggleRuleDay = (roundIdx, ruleIdx, field, value) => {
    setEditForm((f) => ({
      ...f,
      rounds: f.rounds.map((r, i) =>
        i === roundIdx
          ? {
              ...r,
              rules: r.rules.map((rule, j) =>
                j === ruleIdx
                  ? {
                      ...rule,
                      [field]: rule[field].includes(value)
                        ? rule[field].filter((v) => v !== value)
                        : [...rule[field], value].sort((a, b) => a - b),
                    }
                  : rule,
              ),
            }
          : r,
      ),
    }));
  };

  const buildRoundsPayload = () => {
    const seenRounds = new Set();
    const rounds = [];
    for (const r of editForm.rounds) {
      const roundNum = Number(r.round);
      if (!Number.isInteger(roundNum) || roundNum <= 0) {
        toast.error(t("lotto_service_round_number_invalid"), {
          position: "top-right",
          autoClose: 3000,
        });
        return null;
      }
      if (seenRounds.has(roundNum)) {
        toast.error(
          `${t("lotto_service_round_prefix")} ${roundNum} ${t(
            "lotto_service_round_duplicate_suffix",
          )}`,
          {
            position: "top-right",
            autoClose: 3000,
          },
        );
        return null;
      }
      seenRounds.add(roundNum);

      if (r.rules.length === 0) {
        toast.error(
          `${t("lotto_service_round_prefix")} ${roundNum} ${t(
            "lotto_service_round_needs_time_suffix",
          )}`,
          {
            position: "top-right",
            autoClose: 3000,
          },
        );
        return null;
      }

      const round_date = [];
      for (const rule of r.rules) {
        if (!rule.start_time || !rule.end_time) {
          toast.error(
            `${t("lotto_service_round_prefix")} ${roundNum}: ${t(
              "lotto_service_round_time_required_suffix",
            )}`,
            {
              position: "top-right",
              autoClose: 3000,
            },
          );
          return null;
        }
        if (rule.mode === "days_of_month" && rule.daysOfMonth.length === 0) {
          toast.error(
            `${t("lotto_service_round_prefix")} ${roundNum}: ${t(
              "lotto_service_round_days_of_month_required_suffix",
            )}`,
            {
              position: "top-right",
              autoClose: 3000,
            },
          );
          return null;
        }
        if (rule.mode === "days_of_week" && rule.daysOfWeek.length === 0) {
          toast.error(
            `${t("lotto_service_round_prefix")} ${roundNum}: ${t(
              "lotto_service_round_days_of_week_required_suffix",
            )}`,
            {
              position: "top-right",
              autoClose: 3000,
            },
          );
          return null;
        }
        round_date.push({
          start_time: rule.start_time,
          end_time: rule.end_time,
          ...(rule.mode === "days_of_month"
            ? { days_of_month: rule.daysOfMonth }
            : { days_of_week: rule.daysOfWeek }),
        });
      }

      rounds.push({ round: roundNum, round_date });
    }
    return rounds;
  };

  const handleSaveEdit = () => {
    if (!editForm.name.trim()) {
      toast.error(t("lotto_service_name_required"), {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    const rounds = buildRoundsPayload();
    if (rounds === null) return;

    ApiConfig.post("/updateLottoType", {
      id: editForm.id,
      name: editForm.name.trim(),
      rounds,
    }).then((res) => {
      if (res.resCode !== 200) {
        toast.error(res.message || t("lotto_service_edit_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      toast.success(t("lotto_service_edit_success"), { autoClose: 2000 });
      closeEditDialog();
      setReload((r) => r + 1);
    });
  };

  const fetchGroupsForLottoType = (lottoTypeId) => {
    setGroupsLoading(true);
    ApiConfig.get(
      `/getLottoRewardTypeGroups?lottoTypeId=${encodeURIComponent(lottoTypeId)}`,
    ).then((result) => {
      setGroupsLoading(false);
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      setGroupsByLottoTypeId((prev) => ({
        ...prev,
        [lottoTypeId]: Array.isArray(result.data) ? result.data : [],
      }));
    });
  };

  const toggleExpandGroups = (lottoTypeId) => {
    if (expandedId === lottoTypeId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(lottoTypeId);
    if (!groupsByLottoTypeId[lottoTypeId]) {
      fetchGroupsForLottoType(lottoTypeId);
    }
  };

  const handleToggleGroupStatus = (group, checked) => {
    ApiConfig.post("/updateLottoRewardTypeGroupStatus", {
      id: group.id,
      status: checked,
    }).then((res) => {
      if (res.resCode !== 200) {
        toast.error(res.message || t("lotto_service_update_status_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      toast.success(t("lotto_service_update_status_success"), {
        autoClose: 2000,
      });
      fetchGroupsForLottoType(group.lottoType?.id ?? expandedId);
    });
  };

  const pos = localStorage.getItem("position");

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <div className="shrink-0">
        <Navbar />
      </div>
      <div className="flex flex-1 overflow-hidden items-start">
        <div className="shrink-0 overflow-y-auto h-full">
          <Sidebar pos={pos} />
        </div>
        <div className="flex flex-col flex-1 max-h-full overflow-hidden">
          <div className="shrink-0">
            <Header title={t("menu_lotto_onoff")} />
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-2">
            <div className="flex justify-end">
              <button
                onClick={handleCreate}
                className="flex items-center gap-1 bg-gradient-to-b from-brand-gold-300 via-brand-gold-400 to-brand-gold-600 text-brand-ink-onGold border border-brand-gold-200 shadow-gold hover:brightness-105 text-sm font-bold px-3 py-1 rounded"
              >
                <AddIcon style={{ fontSize: 18 }} />
                {t("lotto_service_add_title")}
              </button>
            </div>
            <div className="overflow-x-auto rounded">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-brand-blue-800 text-white">
                    <th className="px-3 py-2 text-left">
                      {t("lotto_service_col_type")}
                    </th>
                    <th className="px-3 py-2 text-left">
                      {t("lotto_service_col_rounds_time")}
                    </th>
                    <th className="px-3 py-2 text-left">
                      {t("lotto_service_col_reward_group")}
                    </th>
                    <th className="px-3 py-2 text-center">
                      {t("lotto_service_col_sale_status")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {lottoTypes.length === 0 ? (
                    <tr>
                      <td
                        colSpan={4}
                        className="text-center py-4 text-gray-400"
                      >
                        {t("common_no_data")}
                      </td>
                    </tr>
                  ) : (
                    lottoTypes.map((lottoType, index) => {
                      const isExpanded = expandedId === lottoType.id;
                      const groups = groupsByLottoTypeId[lottoType.id] || [];
                      const rounds = lottoType.rounds || [];
                      return (
                        <tr
                          key={lottoType.id}
                          className={
                            index % 2 === 0 ? "bg-white" : "bg-gray-50"
                          }
                        >
                          <td className="px-3 py-2">
                            <div className="flex flex-col">
                              <span className="font-bold text-[12px]">
                                {lottoType.name}
                              </span>
                              <span className="text-[11px] text-slate-400">
                                {lottoType.type}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-start gap-2">
                              <div className="flex flex-col gap-0.5">
                                {rounds.length === 0 && (
                                  <span className="text-[11px] text-slate-400">
                                    {t("lotto_service_no_rounds_yet")}
                                  </span>
                                )}
                                {rounds.map((r) => (
                                  <span
                                    key={r.id ?? r.round}
                                    className="text-[13px] text-slate-600"
                                  >
                                    <span className="font-bold text-slate-700">
                                      {t("lotto_service_round_label")}{" "}
                                      {r.round}:
                                    </span>{" "}
                                    {(r.round_date || [])
                                      .map((rule) => formatRule(rule, t))
                                      .join("; ")}
                                  </span>
                                ))}
                              </div>
                              <button
                                onClick={() => openEdit(lottoType)}
                                className="bg-amber-500 text-white hover:bg-amber-600 p-1 rounded flex items-center justify-center shrink-0"
                              >
                                <AccessTimeIcon sx={{ fontSize: 16 }} />
                              </button>
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() =>
                                    toggleExpandGroups(lottoType.id)
                                  }
                                  className="inline-flex items-center gap-1 bg-slate-500 text-white hover:bg-slate-600 text-[11px] font-bold px-2 py-1 rounded"
                                >
                                  {isExpanded ? (
                                    <ExpandLessIcon sx={{ fontSize: 14 }} />
                                  ) : (
                                    <ExpandMoreIcon sx={{ fontSize: 14 }} />
                                  )}
                                  {t("common_details")}
                                </button>
                                <a
                                  href={`/setting/lotto-type?lottoTypeId=${lottoType.id}`}
                                  className="inline-flex items-center gap-1 bg-brand-blue-600 text-white hover:bg-brand-blue-700 text-[11px] font-bold px-2 py-1 rounded no-underline"
                                >
                                  <CategoryIcon sx={{ fontSize: 14 }} />
                                  {t("lotto_service_manage_reward_group")}
                                </a>
                              </div>
                              {isExpanded && (
                                <div className="flex flex-col gap-1 bg-slate-50 border border-slate-200 rounded p-2 min-w-[220px]">
                                  {groupsLoading && groups.length === 0 && (
                                    <span className="text-[11px] text-slate-400">
                                      {t("common_loading")}
                                    </span>
                                  )}
                                  {!groupsLoading && groups.length === 0 && (
                                    <span className="text-[11px] text-slate-400">
                                      {t("lotto_service_no_reward_groups_yet")}
                                    </span>
                                  )}
                                  {groups.map((group) => (
                                    <div
                                      key={group.id}
                                      className="flex items-center justify-between gap-2 bg-white rounded px-2 py-1 border border-slate-200"
                                    >
                                      <div className="flex flex-col">
                                        <span className="text-[11px] font-bold text-slate-700">
                                          {group.name}
                                        </span>
                                        <span className="text-[10px] text-slate-400">
                                          {(group.rewardTypes || []).length}{" "}
                                          {t("common_items_unit")}
                                        </span>
                                      </div>
                                      <Switch
                                        size="small"
                                        checked={!!group.status}
                                        onChange={(e) =>
                                          handleToggleGroupStatus(
                                            group,
                                            e.target.checked,
                                          )
                                        }
                                        inputProps={{
                                          "aria-label": "controlled",
                                        }}
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <span
                                className={`py-1 px-3 rounded text-[11px] font-bold ${
                                  lottoType.status
                                    ? "bg-green-500 text-white"
                                    : "bg-red-500 text-white"
                                }`}
                              >
                                {lottoType.status
                                  ? t("lotto_service_status_open")
                                  : t("lotto_service_status_closed")}
                              </span>
                              <Switch
                                checked={!!lottoType.status}
                                onChange={(e) =>
                                  handleToggleStatus(
                                    lottoType,
                                    e.target.checked,
                                  )
                                }
                                inputProps={{ "aria-label": "controlled" }}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        open={editDialogOpen}
        onClose={closeEditDialog}
        maxWidth="md"
        fullWidth
      >
        <div className="p-4 space-y-3">
          <h2 className="text-lg font-bold text-brand-blue-700">
            {t("lotto_service_edit_title")}
          </h2>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-semibold">
              {t("lotto_service_name_label")}
            </label>
            <input
              type="text"
              value={editForm.name}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, name: e.target.value }))
              }
              className="border border-gray-300 rounded px-2 py-1 text-sm"
            />
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-slate-200">
            <label className="text-sm font-semibold">
              {t("lotto_service_rounds_schedule_label")}
            </label>
            <button
              type="button"
              onClick={addRound}
              className="text-[12px] font-bold px-2 py-1 rounded bg-brand-blue-600 hover:bg-brand-blue-700 text-white"
            >
              {t("lotto_service_add_round_button")}
            </button>
          </div>

          {editForm.rounds.length === 0 && (
            <p className="text-xs text-slate-400">
              {t("lotto_service_no_rounds_yet_short")}
            </p>
          )}

          <div className="space-y-3 max-h-[45vh] overflow-y-auto">
            {editForm.rounds.map((round, roundIdx) => (
              <div
                key={roundIdx}
                className="border border-slate-200 rounded p-2 space-y-2 bg-slate-50"
              >
                <div className="flex items-center gap-2">
                  <label className="text-xs font-semibold">
                    {t("lotto_service_round_prefix")}
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={round.round}
                    onChange={(e) =>
                      updateRoundNumber(roundIdx, e.target.value)
                    }
                    className="border border-gray-300 rounded px-2 py-1 text-sm w-20"
                  />
                  <button
                    type="button"
                    onClick={() => removeRound(roundIdx)}
                    className="ml-auto text-red-600 text-xs font-bold"
                  >
                    {t("lotto_service_delete_round_button")}
                  </button>
                </div>

                {round.rules.map((rule, ruleIdx) => (
                  <div
                    key={ruleIdx}
                    className="border border-slate-300 rounded p-2 bg-white space-y-1"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <select
                        value={rule.mode}
                        onChange={(e) =>
                          updateRule(roundIdx, ruleIdx, {
                            mode: e.target.value,
                          })
                        }
                        className="border border-gray-300 rounded px-1 py-0.5 text-xs"
                      >
                        <option value="days_of_month">
                          {t("lotto_service_mode_days_of_month")}
                        </option>
                        <option value="days_of_week">
                          {t("lotto_service_mode_days_of_week")}
                        </option>
                      </select>
                      <input
                        type="time"
                        value={rule.start_time}
                        onChange={(e) =>
                          updateRule(roundIdx, ruleIdx, {
                            start_time: e.target.value,
                          })
                        }
                        className="border border-gray-300 rounded px-1 py-0.5 text-xs"
                      />
                      <span className="text-xs">{t("common_to")}</span>
                      <input
                        type="time"
                        value={rule.end_time}
                        onChange={(e) =>
                          updateRule(roundIdx, ruleIdx, {
                            end_time: e.target.value,
                          })
                        }
                        className="border border-gray-300 rounded px-1 py-0.5 text-xs"
                      />
                      <button
                        type="button"
                        onClick={() => removeRule(roundIdx, ruleIdx)}
                        className="ml-auto text-red-500 text-xs font-bold"
                      >
                        ✕
                      </button>
                    </div>
                    {rule.mode === "days_of_month" ? (
                      <div className="grid grid-cols-10 gap-1">
                        {DAYS_OF_MONTH.map((d) => (
                          <button
                            type="button"
                            key={d}
                            onClick={() =>
                              toggleRuleDay(roundIdx, ruleIdx, "daysOfMonth", d)
                            }
                            className={`text-[11px] rounded px-1 py-1 ${
                              rule.daysOfMonth.includes(d)
                                ? "bg-brand-blue-600 text-white"
                                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                            }`}
                          >
                            {d}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {WEEKDAY_LABEL_KEYS.map((labelKey, dayIdx) => (
                          <button
                            type="button"
                            key={dayIdx}
                            onClick={() =>
                              toggleRuleDay(
                                roundIdx,
                                ruleIdx,
                                "daysOfWeek",
                                dayIdx,
                              )
                            }
                            className={`text-[11px] rounded px-2 py-1 ${
                              rule.daysOfWeek.includes(dayIdx)
                                ? "bg-brand-blue-600 text-white"
                                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                            }`}
                          >
                            {t(labelKey)}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addRule(roundIdx)}
                  className="text-[12px] font-bold text-brand-blue-600"
                >
                  {t("lotto_service_add_rule_button")}
                </button>
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
            <button
              onClick={closeEditDialog}
              className="text-sm font-bold px-3 py-1 rounded border border-slate-300"
            >
              {t("common_cancel")}
            </button>
            <button
              onClick={handleSaveEdit}
              className="text-sm font-bold px-3 py-1 rounded bg-gradient-to-b from-brand-gold-300 via-brand-gold-400 to-brand-gold-600 text-brand-ink-onGold border border-brand-gold-200 shadow-gold hover:brightness-105"
            >
              {t("membertable_save")}
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

export default LottoService;
